// PLAT-2857 — shared Plaid ownership-upgrade handling for SCC flows (Collect submit, Confirm
// accept). The request is already complete (CertifID, ownership not validated) before the modal
// opens, so every Plaid outcome — match, mismatch, exit, error — ends by calling `onDone`.
// Completion calls are best-effort: failures are logged, never surfaced.
import { ref } from 'vue';
import {
    completeSccPlaidFailureApi,
    completeSccPlaidSuccessApi,
} from '@Api/requestFlow.api';
import type { PlaidLinkExitAction, PlaidLinkOnSuccessMetadata } from '@Types/plaid';

export default function useSccPlaidUpgrade(onDone: () => void | Promise<unknown>) {
    const plaidLinkToken = ref<string | null>(null);
    const isPlaidModalOpen = ref(false);
    // PLAT-3131: the priming step stands between the offer and Plaid's own overlay. Plaid asks
    // for bank credentials, which is a jarring thing to be handed with no warning — this says
    // what is about to happen, why, and that the credentials are never shared, before any of it.
    const isPrimingPlaid = ref(false);
    // True from the moment Plaid hands back control until the completion call finishes.
    // The review views show a full-page loader during this window so the participant never
    // sees the review screen again between Plaid closing and the flow advancing.
    const isCompletingPlaid = ref(false);

    /**
     * PLAT-3131: show the priming step. The request is already complete and certified by now, so
     * this is an offer rather than a demand — nothing here is on the critical path.
     */
    const offerPlaid = (linkToken: string): void => {
        plaidLinkToken.value = linkToken;
        isPrimingPlaid.value = true;
    };

    /** The participant accepted the offer, from the priming step or from the explainer sheet. */
    const continueToPlaid = (): void => {
        isPrimingPlaid.value = false;
        isPlaidModalOpen.value = true;
    };

    const onPlaidSuccess = async (publicToken: string, _metadata: PlaidLinkOnSuccessMetadata): Promise<void> => {
        isCompletingPlaid.value = true;
        try {
            await completeSccPlaidSuccessApi(publicToken);
        } catch (err) {
            console.error('[useSccPlaidUpgrade] CompleteSccPlaidSuccess failed:', err);
        } finally {
            await closePlaidAndFinish();
        }
    };

    const onPlaidExit = async (isUserExit: boolean, exitAction: PlaidLinkExitAction): Promise<void> => {
        isCompletingPlaid.value = true;
        try {
            await completeSccPlaidFailureApi(isUserExit ? 'user_exit' : 'error', {
                errorType: exitAction.error?.errorType,
                errorCode: exitAction.error?.errorCode,
                institutionName: exitAction.metadata?.institution?.name,
                status: exitAction.metadata?.status,
                linkSessionId: exitAction.metadata?.linkSessionId,
            });
        } catch (err) {
            console.error('[useSccPlaidUpgrade] CompleteSccPlaidFailure failed:', err);
        } finally {
            await closePlaidAndFinish();
        }
    };

    const closePlaidAndFinish = async (): Promise<void> => {
        isPlaidModalOpen.value = false;
        plaidLinkToken.value = null;
        try {
            // Keep the loader up until the router guard finishes and the view actually swaps.
            // Clearing at POST-completion re-exposed the review screen for the guard's ~2s
            // (syncRequestSession + LoadRequestFlow run before vue-router mounts the next view).
            await onDone();
        } catch (err) {
            // router.push rejects when a guard throws — swallow it like every other
            // failure here so the modal's emit handler never sees a rejected promise.
            console.error('[useSccPlaidUpgrade] navigation after Plaid completion failed:', err);
        } finally {
            // If navigation aborts or fails, drop back to the review screen rather than
            // leaving the loader stuck forever.
            isCompletingPlaid.value = false;
        }
    };

    return {
        plaidLinkToken,
        isPlaidModalOpen,
        isPrimingPlaid,
        isCompletingPlaid,
        offerPlaid,
        continueToPlaid,
        onPlaidSuccess,
        onPlaidExit,
    };
}
